import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@ui/lib/utils'

const statusBadgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-md border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'bg-neutral-surface text-neutral-strong',
        success: 'bg-success-surface text-success-strong',
        warning: 'bg-warning-surface text-warning-strong',
        error: 'bg-destructive-surface text-destructive-strong',
        info: 'bg-info-surface text-info-strong',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
)

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusBadgeVariants> {
  /**
   * Wird zusätzlich als unsichtbarer Text ausgegeben, wenn die Farbe allein
   * die Bedeutung trägt. Ohne das ist der Status für Screenreader und
   * farbfehlsichtige Nutzer nicht erkennbar.
   */
  srLabel?: string
}

/**
 * Statuskennzeichnung für Listen und Detailseiten. Ruhige Fläche, kräftiger
 * Text — bleibt auch auf schlechten Monitoren lesbar (mindestens 6.7:1).
 *
 * @example
 * ```tsx
 * <StatusBadge tone="success">Abgeschlossen</StatusBadge>
 * <StatusBadge tone="warning">Fällig</StatusBadge>
 * <StatusBadge tone="error" srLabel="Status: Fehler">Fehler</StatusBadge>
 * ```
 */
export function StatusBadge({
  className,
  tone,
  srLabel,
  children,
  ...props
}: StatusBadgeProps) {
  return (
    <span className={cn(statusBadgeVariants({ tone }), className)} {...props}>
      {srLabel && <span className="sr-only">{srLabel}</span>}
      {children}
    </span>
  )
}

export { statusBadgeVariants }
